import React, { useEffect, useState } from "react";
import { Button, Card, List, Space, Tag, Typography } from "antd";
import { Link, useParams } from "react-router-dom";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "@dowty/ui";
import { TournamentStatus } from "../generated/bpong_pb";
import { listTournaments as listClassicTournaments } from "../services/beerpongService";
import type { Tournament as ClassicTournament } from "../services/beerpongService";
import { fetchActiveEvent } from "../services/drinks/EventService";
import type { EventSummary } from "../services/drinkService";
import { useAuth } from "../context/AuthContext";
import { listTournaments } from "./client";
import type { TournamentSummary } from "./client";
import { CardHeading, ListItemMeta } from "../components/ListItemMeta";
import { useTranslation } from "react-i18next";

const EventTournamentsPage: React.FC = () => {
    const { t } = useTranslation();
    const tournamentMeta = (item: TournamentSummary) => {
        const discipline = t("tournaments.discipline." + item.discipline, { defaultValue: item.discipline });
        const status = t("tournamentUi.status." + item.status.toLowerCase(), { defaultValue: item.status });
        return `${discipline} · ${status}`;
    };
    const { eventId: routeEventId } = useParams<{ eventId?: string }>();
    const { user } = useAuth();
    const [event, setEvent] = useState<EventSummary | null>(null);
    const [tournaments, setTournaments] = useState<TournamentSummary[]>([]);
    const [classic, setClassic] = useState<ClassicTournament[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);
    const parsedEventId = Number(routeEventId);

    useEffect(() => {
        let active = true;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                let activeEvent: EventSummary | null = null;
                if (!routeEventId && user) activeEvent = await fetchActiveEvent(user);
                if (!active) return;
                const context = Number.isSafeInteger(parsedEventId) && parsedEventId > 0
                    ? parsedEventId
                    : activeEvent?.eventId ?? null;
                if (context === null) {
                    setEvent(null);
                    setTournaments([]);
                    setClassic([]);
                    return;
                }
                const [next, old] = await Promise.all([listTournaments(context, user), listClassicTournaments(user ?? undefined, context)]);
                if (!active) return;
                setEvent(activeEvent);
                setTournaments(next);
                setClassic(old);
            } catch (reason) {
                if (active) setError(reason instanceof Error ? reason.message : t("tournaments.loadError"));
            } finally {
                if (active) setLoading(false);
            }
        };
        void load();
        return () => { active = false; };
    }, [attempt, parsedEventId, routeEventId, t, user]);

    if (loading) return <div className="tournament-page-content"><LoadingState variant="list" label={t("tournaments.loading")} /></div>;
    if (error) return <div className="tournament-page-content"><ErrorState headingLevel={1} message={error} retry={() => setAttempt((current) => current + 1)} retryLabel={t("tournaments.retry")} /></div>;
    if (!routeEventId && !event) return <div className="tournament-page-content"><EmptyState headingLevel={1} title={t("tournaments.noActiveEvent.title")} description={t("tournaments.noActiveEvent.description")} /></div>;

    let contextId = event?.eventId ?? null;
    if (routeEventId) contextId = Number.isSafeInteger(parsedEventId) && parsedEventId > 0 ? parsedEventId : null;
    if (contextId === null) return <div className="tournament-page-content"><EmptyState headingLevel={1} title={t("tournaments.eventNotFound.title")} description={t("tournaments.eventNotFound.description")} /></div>;
    const newTournaments = tournaments.filter((item) => item.status === "Draft");
    const runningTournaments = tournaments.filter((item) => item.status === "Running");
    const completedTournaments = tournaments.filter((item) => item.status === "Completed" || item.status === "Cancelled");
    const completedClassic = classic.filter((item) => item.status === TournamentStatus.TOURNAMENT_STATUS_COMPLETED);
    const activeClassic = classic.filter((item) => item.status !== TournamentStatus.TOURNAMENT_STATUS_COMPLETED);

    return <Space direction="vertical" size="large" className="tournament-page-content">
        <PageHeader title={t("tournaments.title")} subtitle={event?.eventName ?? t("tournaments.eventFallback", { id: contextId })} extra={<Button type="primary" href={`/tournaments/new?eventId=${contextId}`}>{t("tournaments.create")}</Button>} />
        <Card title={<CardHeading>{t("tournaments.newSection")}</CardHeading>}>
            {newTournaments.length ? (
                <List
                    dataSource={newTournaments}
                    renderItem={(item) => (
                        <List.Item
                            actions={[
                                <Link key="open" to={`/tournaments/${item.id}`}>{t("tournaments.open")}</Link>,
                            ]}
                        >
                            <ListItemMeta title={item.name}>{tournamentMeta(item)}</ListItemMeta>
                        </List.Item>
                    )}
                />
            ) : <Typography.Text type="secondary">{t("tournaments.noneNew")}</Typography.Text>}
        </Card>
        <Card title={<CardHeading>{t("tournaments.runningSection")}</CardHeading>}>
            {runningTournaments.length ? (
                <List dataSource={runningTournaments} renderItem={(item) => (
                    <List.Item actions={[<Link key="open" to={`/tournaments/${item.id}`}>{t("tournaments.open")}</Link>]}>
                        <ListItemMeta title={item.name}>{tournamentMeta(item)}</ListItemMeta>
                    </List.Item>
                )} />
            ) : <Typography.Text type="secondary">{t("tournaments.noneRunning")}</Typography.Text>}
        </Card>
        <Card title={<CardHeading>{t("tournaments.older")}</CardHeading>}>
            {completedTournaments.length > 0 && <List dataSource={completedTournaments} renderItem={(item) => (
                <List.Item actions={[<Link key="open" to={`/tournaments/${item.id}`}>{t("tournaments.open")}</Link>]}>
                    <ListItemMeta title={item.name}>{tournamentMeta(item)}</ListItemMeta>
                </List.Item>
            )} />}
            {completedTournaments.length === 0 && completedClassic.length === 0 && <Typography.Text type="secondary">{t("tournaments.noneCompleted")}</Typography.Text>}
            {completedClassic.length ? (
                <List
                    dataSource={completedClassic}
                    renderItem={(item) => (
                        <List.Item
                            actions={[
                                <Link key="open" to="/beerpong/tournaments">{t("tournaments.openClassic")}</Link>,
                            ]}
                        >
                            <ListItemMeta title={<Space>{item.tournamentName}<Tag>{t("tournaments.classic")}</Tag></Space>}>
                                {t("tournaments.completed")}
                            </ListItemMeta>
                        </List.Item>
                    )}
                />
            ) : <Typography.Text type="secondary">{t("tournaments.noneClassic")}</Typography.Text>}
        </Card>
        {activeClassic.length > 0 && <Card title={<CardHeading>{t("tournaments.inProgressClassic")}</CardHeading>}>
            <List dataSource={activeClassic} renderItem={(item) => (
                <List.Item
                    actions={[<Link key="open" to="/beerpong/tournaments">{t("tournaments.openClassic")}</Link>]}
                >
                    <ListItemMeta title={<Space>{item.tournamentName}<Tag>{t("tournaments.classic")}</Tag></Space>}>
                        {t("tournaments.inProgress")}
                    </ListItemMeta>
                </List.Item>
            )} />
        </Card>}
    </Space>;
};

export default EventTournamentsPage;
